import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Grid,
  Snackbar,
  Alert,
  Modal,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TuneIcon from '@mui/icons-material/Tune';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditIcon from '@mui/icons-material/Edit';
import { exchangeRates, SAVE_USER_DATA_LAMBDA_URL } from 'src/const';
import { User } from 'src/interfaces/iuser';
import {
  generateDownloadFilename,
  processArrayToCsv,
  processMarketData,
  processReportData,
} from './helper';
import { formatCurrency, makeDownloadableBlob } from 'src/utils/functions';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import KPIToggleModal from 'src/App/Modals/KPIToggleModal';
import { COLOR_GREY_200, COLOR_PRIMARY_1 } from 'src/colors';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { callApi } from 'src/core/callApi';
import KpiChart from './KpiChart';

const MarketDataGrid = ({
  data,
  currency,
  user,
  handleOnSampleClick,
  handleComparisonWrapper,
  comparisons,
  isMobileScreen,
  source,
  marketComparisons,
  savedMarketComparisonConfig,
  setCurrentSavedComparison,
  setComparison
}: {
  data: any;
  currency: string;
  user: User;
  handleOnSampleClick: any;
  handleComparisonWrapper: any;
  comparisons: { label: string; details: any }[];
  isMobileScreen: boolean;
  source: 'market' | 'report';
  marketComparisons: any[];
  savedMarketComparisonConfig?: {
    subsectionKey: string;
    visibleKeys: string[];
  }[];
  setCurrentSavedComparison: React.Dispatch<React.SetStateAction<any>>;
  setComparison: React.Dispatch<React.SetStateAction<{
    label: string;
    details: any;
  }[]>>;
}) => {
  const currencySymbol = exchangeRates[currency].symbol;
  const [selectedBedroom, setSelectedBedroom] = useState<string | number>('any');
  const [rowData, setRowData] = useState<any[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [showSaveMarketComparisonsModal, setShowSaveMarketComparisonsModal] = useState(false);
  const [isCompareSaving, setIsCompareSaving] = useState(false);

  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [propertyPriceMarketWise, setPropertyPriceMarketWise] = useState<{
    [key: string]: number;
  }>({});

  const baseColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 150,
    },
    {
      field: 'occupancy_rate',
      headerName: 'Occupancy (%)',
      type: 'number',
      flex: 1,
      minWidth: 140,
      renderCell: params => {
        return params.row.isCacheOutdated ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
            }}
          >
            ⌛ Processing... (~5 min){' '}
            <Button
              variant="contained"
              size="small"
              sx={{ color: '#fff' }}
              onClick={handleComparisonWrapper}
            >
              Check Again
            </Button>
          </Box>
        ) : (
          params.value
        );
      },
      colSpan: params => (params.row.isCacheOutdated ? 4 : 1),
    },
    {
      field: 'adr',
      headerName: `ADR (${currencySymbol})`,
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderCell: params =>
        params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
      colSpan: () => 0,
    },
    {
      field: 'revenue',
      headerName: `Revenue (${currencySymbol})`,
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderCell: params =>
        params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
      colSpan: () => 0,
    },
    {
      field: 'listings',
      headerName: 'Listings',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderCell: params =>
        params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
      colSpan: () => 0,
    },
    {
      field: 'property_price',
      headerName: `Property Price (${currencySymbol})`,
      type: 'number',
      flex: 1,
      minWidth: 140,
      editable: true,
      renderCell: params => {
        if (params.row.isCacheOutdated) return '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {formatCurrency(String(params.value))}
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                const api = params.api;
                const cellParams = api.getCellParams(params.id, params.field);
                api.setCellMode(params.id, params.field, 'edit');
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'yield',
      headerName: 'Yield (%)',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        if (params.row.isCacheOutdated) return '';
    
        const revenue = Number(params.row.revenue) || 0;
        const price = Number(params.row.property_price);
    
        if (!price || price <= 0) {
          return (
            <TooltipNew title="Enter Property Price to calculate yield" content={''} placement="top">
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#999' }}>-</span>
              </Box>
            </TooltipNew>
          );
        }
    
        const calculatedYield = ((revenue / price) * 100).toFixed(2);
        return `${calculatedYield}%`;
      },
    }
    
  ];

  const [marketCompareColumns, setMarketCompareColumns] = useState<GridColDef[]>(baseColumns);

  const [groupedMetrics, setGroupedMetrics] = useState([
    {
      subsection: 'Key Performance Indicators (KPIs)',
      subsectionKey: 'kpiMetrics',
      metrics: [
        { name: `ADR (Median) (${currencySymbol})`, isVisible: true, isAllowed: true, key: 'adr' },
        {
          name: `ADR (25th Percentile) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'adr_25th',
        },
        {
          name: `ADR (75th Percentile) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'adr_75th',
        },
        {
          name: `ADR (90th Percentile) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'adr_90th',
        },
        {
          name: 'Occupancy Rate (Median) (%)',
          isVisible: true,
          isAllowed: true,
          key: 'occupancy_rate',
        },
        {
          name: 'Occupancy Rate (25th Percentile) (%)',
          isVisible: true,
          isAllowed: true,
          key: 'occupancy_rate_25th',
        },
        {
          name: 'Occupancy Rate (75th Percentile) (%)',
          isVisible: true,
          isAllowed: true,
          key: 'occupancy_rate_75th',
        },
        {
          name: 'Occupancy Rate (90th Percentile) (%)',
          isVisible: true,
          isAllowed: true,
          key: 'occupancy_rate_90th',
        },
        {
          name: `Revenue (Median) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'revenue',
        },
        {
          name: `Revenue (25th Percentile) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'revenue_25th',
        },
        {
          name: `Revenue (75th Percentile) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'revenue_75th',
        },
        {
          name: `Revenue (90th Percentile) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'revenue_90th',
        },
        { name: 'Listings', isVisible: true, isAllowed: true, key: 'listings' },
      ],
    },
    {
      subsection: 'Year-over-Year (YoY) Changes',
      subsectionKey: 'yoyChanges',
      metrics: [
        {
          name: 'Occupancy Rate YoY Change (%)',
          isVisible: true,
          isAllowed: true,
          key: 'occupancy_rate_yoy',
        },
        { name: 'ADR YoY Change (%)', isVisible: true, isAllowed: true, key: 'adr_yoy' },
        { name: 'Revenue YoY Change (%)', isVisible: true, isAllowed: true, key: 'revenue_yoy' },
        { name: 'Supply YoY Change (%)', isVisible: true, isAllowed: true, key: 'supply_yoy' },
        {
          name: 'supply still available from last year (%)',
          isVisible: true,
          isAllowed: true,
          key: 'common_supply_share',
        },
      ],
    },
    {
      subsection: 'Two-Year Changes',
      subsectionKey: 'twoYearChanges',
      metrics: [
        {
          name: 'Occupancy Rate 2-Year Change (%)',
          isVisible: true,
          isAllowed: true,
          key: 'occupancy_rate_2yr',
        },
        { name: 'ADR 2-Year Change (%)', isVisible: true, isAllowed: true, key: 'adr_2yr' },
        {
          name: 'Revenue 2-Year Change (%)',
          isVisible: true,
          isAllowed: true,
          key: 'revenue_2yr',
        },
        { name: 'Supply 2-Year Change (%)', isVisible: true, isAllowed: true, key: 'supply_2yr' },
      ],
    },
    {
      subsection: 'Additional Metrics',
      subsectionKey: 'additionalMetrics',
      metrics: [
        {
          name: `Cleaning Fees (Median) (${currencySymbol})`,
          isVisible: true,
          isAllowed: true,
          key: 'cleaning_fee',
        },
        {
          name: 'Market Strictness',
          isVisible: true,
          isAllowed: true,
          key: 'license_percentage',
        },
        {
          name: '% of Hosts with 1-5 Listings',
          isVisible: true,
          isAllowed: true,
          key: 'hosts_1_5',
        },
        {
          name: '% of Hosts with 6-20 Listings',
          isVisible: true,
          isAllowed: true,
          key: 'hosts_6_20',
        },
        {
          name: '% of Hosts with 20+ Listings',
          isVisible: true,
          isAllowed: true,
          key: 'hosts_20plus',
        },
        {
          name: '% of Listings with Pool',
          isVisible: true,
          isAllowed: true,
          key: 'listings_pool',
        },
        {
          name: '% of Listings with Hot Tub',
          isVisible: true,
          isAllowed: true,
          key: 'listings_hot_tub',
        },
      ],
    },
  ]);

  // Add this custom toolbar component
  const CustomToolbar = ({ fileName }) => {
    return (
      <Box
        sx={{
          position: 'sticky !important',
          left: 0,
          width: '300px',
          maxWidth: '300px',
        }}
      >
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport csvOptions={{ fileName: fileName, utf8WithBom: true }} />
        </GridToolbarContainer>
      </Box>
    );
  };

  useEffect(() => {
    if (savedMarketComparisonConfig) {
      setGroupedMetrics(prev =>
        prev.map(group => {
          const savedGroup = savedMarketComparisonConfig.find(
            g => g.subsectionKey === group.subsectionKey
          );
          if (!savedGroup) return group;

          return {
            ...group,
            metrics: group.metrics.map(metric => ({
              ...metric,
              isVisible: savedGroup.visibleKeys.includes(metric.key),
            })),
          };
        })
      );
    }
  }, [savedMarketComparisonConfig]);

  interface Metric {
    name: string;
    isVisible: boolean;
    isAllowed: boolean;
    key: string;
  }

  interface ColumnDefinitionParams {
    currencySymbol: string;
    handleComparisonWrapper: () => void;
    formatCurrency: (value: string) => string;
  }

  const getColumnDefinition = (
    metric: Metric,
    { currencySymbol, handleComparisonWrapper, formatCurrency }: ColumnDefinitionParams
  ): GridColDef => {
    const columnHeader = metric.name || '';
    const estimatedCharWidth = 7;
    const padding = 6;
    const baseWidth = columnHeader.length * estimatedCharWidth + padding;

    switch (metric.key) {
      case 'occupancy_rate':
        return {
          field: metric.key,
          headerName: 'Occupancy (%)',
          type: 'number',
          flex: 1,
          renderCell: params =>
            params.row.isCacheOutdated ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                }}
              >
                ⌛ Processing... (~5 min)
                <Button
                  variant="contained"
                  size="small"
                  sx={{ color: '#fff' }}
                  onClick={handleComparisonWrapper}
                >
                  Check Again
                </Button>
              </Box>
            ) : (
              params.value
            ),
          colSpan: params => (params.row.isCacheOutdated ? 4 : 1),
        };
      case 'adr':
        return {
          field: metric.key,
          headerName: `ADR (${currencySymbol})`,
          type: 'number',
          flex: 1,
          minWidth: 100,
          renderCell: params =>
            params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
          colSpan: () => 0,
        };
      case 'revenue':
        return {
          field: metric.key,
          headerName: `Revenue (${currencySymbol})`,
          type: 'number',
          flex: 1,
          minWidth: 80,
          renderCell: params =>
            params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
          colSpan: () => 0,
        };
      case 'listings':
        return {
          field: metric.key,
          headerName: 'Listings',
          type: 'number',
          flex: 1,
          minWidth: 80,
          renderCell: params =>
            params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
          colSpan: () => 0,
        };
      default:
        return {
          field: metric.key,
          headerName: `${metric.name}`,
          type: 'number',
          flex: 1,
          minWidth: baseWidth,
          renderCell: params =>
            params.row.isCacheOutdated ? '' : formatCurrency(String(params.value)),
          colSpan: () => 0,
        };
    }
  };

  useEffect(() => {
    if (selectedBedroom === 'any') {
      // Get keys of the base columns to avoid duplicates.
      const baseColumnKeys = baseColumns.map(col => col.field);

      // Generate additional columns based on groupedMetrics, filtering out duplicates.
      const additionalColumns = groupedMetrics.flatMap(group =>
        group.metrics
          .filter(
            metric => metric.isAllowed && metric.isVisible && !baseColumnKeys.includes(metric.key)
          )
          .map(metric =>
            getColumnDefinition(metric, {
              currencySymbol,
              handleComparisonWrapper,
              formatCurrency,
            })
          )
      );
      setMarketCompareColumns([...baseColumns, ...additionalColumns]);
    } else {
      setMarketCompareColumns([...baseColumns]);
    }
  }, [groupedMetrics, selectedBedroom, currency]);

  const ReportCompareColumns = [
    { field: 'name', headerName: 'Name', flex: 2 },
    { field: 'bedrooms', headerName: 'Bedrooms', flex: 1 },
    { field: 'occupancy_rate', headerName: 'Occupancy (%)', type: 'number', flex: 1 },
    { field: 'adr', headerName: `ADR (${currencySymbol})`, type: 'number', flex: 1 },
    { field: 'revenue', headerName: `Revenue (${currencySymbol})`, type: 'number', flex: 1 },
  ];

  useEffect(() => {
    setRowData(
      source === 'market'
        ? processMarketData(data, selectedBedroom, currency, comparisons, propertyPriceMarketWise, setComparison)
        : processReportData(data, currency)
    );
  }, [data, selectedBedroom, currency, propertyPriceMarketWise]);

  const getAddMoreCompsButton = () => {
    const isDisabled = selectedBedroom !== 'any';

    const moreCompsButton = (
      <IconButton
        sx={{ background: COLOR_GREY_200, ...(isDisabled ? { opacity: 0.5 } : {}) }}
        onClick={() => !isDisabled && setShowFilter(!showFilter)}
      >
        <TuneIcon />
      </IconButton>
    );

    if (isDisabled) {
      return (
        <TooltipNew
          arrow={false}
          placement="bottom"
          title={'Additional comps cannot currently be added using the bedroom filter.'}
          content={''}
        >
          {moreCompsButton}
        </TooltipNew>
      );
    }

    return moreCompsButton;
  };

  const handleSaveMarketComparisons = async (name: string) => {
    setIsCompareSaving(true);
    try {
      const marketComparisonsMapToSave = marketComparisons.map(comparison => ({
        label: comparison.label,
        details: {
          name: comparison.details.name,
          city: comparison.details.city,
          countryCode: comparison.details.countryCode,
        },
      }));

      const gridColumnConfig = groupedMetrics.map(group => ({
        subsectionKey: group.subsectionKey,
        visibleKeys: group.metrics.filter(metric => metric.isVisible).map(metric => metric.key),
      }));

      const response = await callApi(
        SAVE_USER_DATA_LAMBDA_URL,
        {
          body: {
            savedMarketComparisons: {
              comparisonsName: name,
              markets: marketComparisonsMapToSave,
              gridColumnConfig,
            },
          },
        },
        true,
        false,
        '',
        'POST',
        true
      );
      console.log('=>: ', response);
      setCurrentSavedComparison(prev => ({...prev, [name]: {
        markets: marketComparisonsMapToSave,
        gridColumnConfig,
      }}));
      setNotification({ message: 'Market comparisons saved successfully', type: 'success' });
    } catch (error) {
      console.error('Error saving market comparisons: ', error);
      setNotification({ message: 'Error saving market comparisons', type: 'error' });
    } finally {
      setIsCompareSaving(false);
    }
  };

  const handleSaveMarketComparisonsWrapper = () => {
    setShowSaveMarketComparisonsModal(true);
  };

  const handleCellEditCommit = (params: any) => {
    if (params.field !== 'property_price') return;

    // Get the row data from rowData state
    const row = rowData.find(r => r.id === params.id);
    if (!row || !row.name) return;

    const marketName = row.name;
    const newValue = Number(params.value) || 0;

    // Update the propertyPriceMarketWise state
    setPropertyPriceMarketWise(prev => ({
      ...prev,
      [marketName]: newValue,
    }));

    // Update the rowData state
    setRowData(prevRows => {
      const updatedRows = [...prevRows];
      const rowIndex = updatedRows.findIndex(r => r.id === params.id);
      if (rowIndex >= 0) {
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          property_price: newValue,
        };
      }
      return updatedRows;
    });
  };

  return (
    <>
      <div
        id="marketCompareDataTable"
        style={{
          // height: 420,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            mb: '10px',
            gap: '12px',
            boxSizing: 'border-box',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box
            justifyContent={{ xs: 'flex-start', md: 'center' }}
            alignItems="center"
            sx={{ gap: '10px', width: { xs: '100%', md: 'auto' } }}
          ></Box>

          <Box
            display="flex"
            justifyContent={{ xs: 'flex-end', md: 'center' }}
            alignItems="center"
            sx={{ gap: '10px', width: { xs: '100%', md: 'auto' } }}
          >
            {source === 'market' && (
              <IconButton
                // variant="outlined"
                onClick={handleSaveMarketComparisonsWrapper}
                disabled={isCompareSaving}
                sx={{
                  background: COLOR_PRIMARY_1 + '33',
                  transition: 'background 0.3s ease',
                  '&:hover': {
                    background: COLOR_PRIMARY_1 + '55',
                  },
                }}
              >
                {isCompareSaving ? (
                  <CircularProgress size={20} />
                ) : (
                  <SaveRoundedIcon
                    sx={{
                      color: COLOR_PRIMARY_1,
                    }}
                  />
                )}
              </IconButton>
            )}
            {source === 'market' && (
              <FormControl sx={{ width: '120px' }} size="small">
                <InputLabel>Bedroom</InputLabel>
                <Select
                  value={selectedBedroom}
                  label="Bedroom"
                  onChange={event => setSelectedBedroom(event.target.value)}
                >
                  {[
                    ['Any', 'any'],
                    ['Studio', 0],
                    [1, 1],
                    [2, 2],
                    [3, 3],
                    [4, 4],
                    [5, 5],
                    ['6+', 6],
                  ].map(bedroom => (
                    <MenuItem value={bedroom[1]}>{bedroom[0]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {rowData && (
          <Box>
            <>
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <DataGrid
                  rows={rowData}
                  columns={
                    source === 'market'
                      ? selectedBedroom === 'any'
                        ? marketCompareColumns
                        : baseColumns
                      : ReportCompareColumns
                  }
                  onCellEditCommit={handleCellEditCommit}
                  processRowUpdate={(newRow, oldRow) => {
                    // This ensures the row is updated only when the edit is complete
                    return newRow;
                  }}
                  sx={{
                    width: '100%',
                    height: isMobileScreen ? '340px' : '400px',
                  }}
                  components={{
                    Toolbar: props => (
                      <CustomToolbar {...props} fileName={generateDownloadFilename()} />
                    ),
                  }}
                  hideFooter
                />
              </Box>

              {source === 'market' && (
                <KpiChart
                  data={data}
                  currency={currency}
                  user={user}
                  handleOnSampleClick={handleOnSampleClick}
                  handleComparisonWrapper={handleComparisonWrapper}
                  comparisons={comparisons}
                  isMobileScreen={isMobileScreen}
                  source={source}
                  marketComparisons={marketComparisons}
                  selectedBedroom={selectedBedroom}
                />
              )}
            </>
          </Box>
        )}
      </div>
      {showFilter && (
        <KPIToggleModal
          isOpen={showFilter}
          onClose={() => setShowFilter(false)}
          title="Market Metrics"
          groupedMetrics={groupedMetrics}
          toggleVisibility={(subsectionKey, metricKey, newVisibility) => {
            setGroupedMetrics(prev => {
              return prev.map(subsection => {
                if (subsection.subsectionKey === subsectionKey) {
                  return {
                    ...subsection,
                    metrics: subsection.metrics.map(metric => {
                      if (metric.key === metricKey) {
                        return { ...metric, isVisible: newVisibility };
                      }
                      return metric;
                    }),
                  };
                }
                return subsection;
              });
            });
          }}
        />
      )}

      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification?.type}>{notification?.message}</Alert>
      </Snackbar>

      {showSaveMarketComparisonsModal && (
        <SaveMarketComparisonsModal
          setShowSaveMarketComparisonsModal={setShowSaveMarketComparisonsModal}
          handleSaveMarketComparisons={handleSaveMarketComparisons}
        />
      )}
    </>
  );
};

const SaveMarketComparisonsModal = ({
  setShowSaveMarketComparisonsModal,
  handleSaveMarketComparisons,
}: {
  setShowSaveMarketComparisonsModal: (show: boolean) => void;
  handleSaveMarketComparisons: (name: string) => Promise<void>;
}) => {
  const [name, setName] = useState('');

  const handleSaveClick = () => {
    if (name.length > 0) {
      const formattedName = name.slice(0, 400).trim(); // Limit name to 400 characters
      setShowSaveMarketComparisonsModal(false);
      handleSaveMarketComparisons(formattedName);
    }
  };

  return (
    <Modal open={true} onClose={() => setShowSaveMarketComparisonsModal(false)}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '500px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: '30px',
          borderRadius: '12px',
        }}
      >
        <IconButton
          onClick={() => setShowSaveMarketComparisonsModal(false)}
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mb: '20px' }}>
          Enter your market comparison name
        </Typography>
        <TextField
          label="Name"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          sx={{ mb: '20px' }}
        />
        <Button variant="contained" onClick={handleSaveClick} sx={{ float: 'right' }}>
          Save
        </Button>
      </Box>
    </Modal>
  );
};
export default MarketDataGrid;
